<script setup lang="ts">
import { computed } from 'vue';

import type { Token } from '@/references/tokens';

import TokenImage from '@/components/TokenImage.vue';
import UiBlinker from '@/components/ui/UiBlinker.vue';
import UiFitty from '@/components/ui/UiFitty.vue';
import UiText from '@/components/ui/UiText.vue';

interface Props {
  topAmount: string;
  topAmountMuted?: boolean;
  topAmountHighlighted?: boolean;
  topAmountBordered?: boolean;
  topAmountStrikeThrough?: boolean;
  topAmountWithSkeleton?: boolean;
  topAmountMaxSize?: number;
  bottomAmount?: string;
  bottomAmountMuted?: boolean;
  bottomAmountToken?: Token;
  bottomAmountWithSkeleton?: boolean;
}

const props = defineProps<Props>();

const maxSize = computed(
  (): number => props.topAmountMaxSize ?? (props.topAmountBordered ? 40 : 64)
);
</script>

<template>
  <div class="ui-amounts">
    <div class="ui-amounts__body">
      <UiFitty :min-size="10" :max-size="maxSize">
        <UiText
          tag="p"
          class="ui-amounts__top-amount"
          :class="{
            'ui-amounts__top-amount_highlighted': props.topAmountHighlighted,
            'ui-amounts__top-amount_bordered': props.topAmountBordered,
            'ui-amounts__top-amount_with-skeleton': props.topAmountWithSkeleton
          }"
          :weight="props.topAmountBordered ? 'semibold' : 'bold'"
          align="center"
          :muted="props.topAmountMuted"
          :line-through="props.topAmountStrikeThrough"
        >
          <UiBlinker :blinking="!!props.topAmountWithSkeleton">
            {{ props.topAmount }}
          </UiBlinker>
        </UiText>
      </UiFitty>
      <div
        v-if="props.bottomAmount !== undefined"
        class="ui-amounts__bottom"
        :class="{
          'ui-amounts__bottom_gap': props.topAmountBordered
        }"
      >
        <TokenImage
          v-if="props.bottomAmountToken"
          class="ui-amounts__bottom-icon"
          :token="props.bottomAmountToken"
          size="sm"
        />
        <UiText
          v-if="props.bottomAmount"
          class="ui-amounts__bottom-amount"
          tag="p"
          :muted="props.bottomAmountMuted"
          size="sm"
          weight="medium"
        >
          <UiBlinker :blinking="!!props.bottomAmountWithSkeleton">
            {{ props.bottomAmount }}
          </UiBlinker>
        </UiText>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ui-amounts {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 24px;
  min-height: 148px;
  word-break: break-word;
  border: 1px dashed var(--color-secondary-stroke);
  border-radius: 20px;
}

.ui-amounts__body {
  width: 100%;
  text-align: center;
}

.ui-amounts__top-amount {
  line-height: 1.2;
  transition: color var(--animation-micro) var(--animation-effect);
}

.ui-amounts__top-amount_highlighted {
  color: var(--color-green);
}

.ui-amounts__top-amount_bordered {
  position: relative;
}

.ui-amounts__top-amount_bordered::after {
  position: absolute;
  top: -4px;
  left: -8px;
  width: calc(100% + 16px);
  height: calc(100% + 8px);
  border: 2px solid var(--color-secondary-stroke);
  border-radius: 12px;
  content: '';
  pointer-events: none;
}

.ui-amounts__top-amount_with-skeleton {
  color: var(--color-secondary-text);
}

.ui-amounts__bottom {
  text-align: center;
}

.ui-amounts__bottom_gap {
  margin: 8px 0 0;
}

.ui-amounts__bottom-icon {
  display: inline-flex;
  margin: 0 4px 0 0;
  vertical-align: middle;
}

.ui-amounts__bottom-amount {
  display: inline;
  vertical-align: middle;
  transition: color var(--animation-micro) var(--animation-effect);
}
</style>
